import { useEffect, useState } from "react";
import { tipoff } from "@/lib/format";

const FINAL_DELAY_MS = 3 * 60 * 1000;
// Finished games move to the end 3 minutes after the final whistle; otherwise tip-off order.
const done = (g, now) => g.status === "post" && (!g.final_at || now - new Date(g.final_at).getTime() >= FINAL_DELAY_MS);
import { MatchupCard, MatchupSkeleton } from "@/components/matchup/MatchupCard";

const athensDay = (iso) => new Date(iso).toLocaleDateString("en-US", { timeZone: "Europe/Athens", month: "short", day: "2-digit" }).toUpperCase();

const projOf = (totals, g, teamId) => totals?.find((t) => t.game_id === g.id && t.team_id === teamId && t.mode === "rotation")?.totals?.pts;

const toCard = (g, totals) => ({
  id: g.id, awayCode: g.away_code, homeCode: g.home_code, awayLogo: g.away_logo, homeLogo: g.home_logo,
  awayWins: g.away_wins, awayLosses: g.away_losses, homeWins: g.home_wins, homeLosses: g.home_losses,
  startTime: tipoff(g.date_utc), startDate: athensDay(g.date_utc),
  status: g.status === "post" ? "final" : g.status === "ppd" ? "ppd" : g.status === "in" ? "live" : "scheduled", liveDetail: g.status_detail, awayScore: g.away_pts, homeScore: g.home_pts,
  h2hAwayWins: g.h2h_away_wins, h2hHomeWins: g.h2h_home_wins, h2hGames: g.h2h_games, lineups: g.lineups_announced,
  awayB2b: g.away_b2b, homeB2b: g.home_b2b,
  projAway: g.status === "pre" ? projOf(totals, g, g.away_team_id) : null, projHome: g.status === "pre" ? projOf(totals, g, g.home_team_id) : null,
});

export function GamePills({ games, value, onChange, loading, teamTotals }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 20000); return () => clearInterval(t); }, []);
  if (loading) {
    return <div className="nba-matchup-strip" data-testid="game-pills-loading">{[0, 1, 2, 3].map((i) => <MatchupSkeleton key={i} />)}</div>;
  }
  if (!games.length) return null;
  return (
    <div className="nba-matchup-strip" data-testid="game-pills">
      <button type="button" data-testid="game-pill-all" aria-pressed={value === "all"} onClick={() => onChange("all")}
        className={`nba-matchup nba-all ${value === "all" ? "is-selected" : ""}`}>
        <div className="nba-center"><strong>ALL</strong><span>{games.length} games</span></div>
      </button>
      {[...games].sort((a, b) => done(a, now) - done(b, now)).map((g) => <MatchupCard key={g.id} game={toCard(g, teamTotals)} selected={value === g.id} onSelect={onChange} />)}
    </div>
  );
}

export function FormBadge({ form }) {
  if (!form || form.pct == null) return <span className="text-slate-600">–</span>;
  const up = form.pct >= 0;
  return (
    <span title={`Last 5: ${form.last5} · Season: ${form.season}`}
      className={`inline-block text-xs px-2 py-0.5 rounded font-mono-num font-medium border ${
        up ? "text-emerald-400 bg-emerald-950/50 border-emerald-800/40" : "text-rose-400 bg-rose-950/50 border-rose-800/40"}`}>
      {form.last5} <span className="opacity-70">{up ? "+" : ""}{form.pct}%</span>
    </span>
  );
}

export function InjuryBadge({ status, id }) {
  const out = /out/i.test(status);
  return (
    <span data-testid={`injury-${id}`} className={`ml-2 align-middle text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border font-bold ${
      out ? "text-rose-300 border-rose-500/40 bg-rose-950/50" : "text-amber-300 border-amber-500/40 bg-amber-950/40"}`}>{status}</span>
  );
}
